import type {FC} from 'react';
import React from 'react';
import type {FieldError, UseFormRegister} from 'react-hook-form';
import {FiAlertCircle} from 'react-icons/fi';
import clsx from 'clsx';

import type {FormInput} from '@/components/content-blocks/contact-form-section/form/FormSchema';

type FormFieldProps = {
	type: string;
	placeholder?: string;
	name: ValidFieldNames;
	label: string;
	register?: UseFormRegister<FormInput>;
	error?: FieldError;
	valueAsNumber?: boolean;
	required?: boolean;
};

export type ValidFieldNames =
	| 'name'
	| 'email'
	| 'message'
	| 'phone'
	| 'businessName';

export const FormField: FC<FormFieldProps> = ({
	type,
	placeholder,
	name,
	label,
	register,
	error,
	valueAsNumber,
	required,
}) => {
	if (type === 'textarea') {
		return (
			<div className={clsx('grid', 'gap-1')}>
				<label
					htmlFor={name}
					className={clsx(
						'text-neutral-text-default',
						'flex',
						'gap-2',
						'justify-between',
					)}
				>
					{label}
					{!required && (
						<span className={clsx('text-neutral-text-subtle')}>
							Valgfritt
						</span>
					)}
				</label>
				<textarea
					id={name}
					placeholder={placeholder}
					{...(required && {'aria-required': true})}
					{...(error && {'aria-invalid': true})}
					{...(register && register(name, {valueAsNumber}))}
					className={clsx(
						'focus-visible:border-primary-border-strong',
						'bg-white',
						'border',
						'rounded-xl',
						'min-h-[112px]',
						error
							? 'border-danger-border-strong'
							: 'border-neutral-border-default',
					)}
				/>
				{error && (
					<span
						role="alert"
						className={clsx(
							'flex',
							'gap-1',
							'mt-1',
							'text-danger-text-subtle',
						)}
					>
						<FiAlertCircle
							aria-hidden
							className="text-danger-text-subtle relative top-1 shrink-0"
						/>
						{error.message}
					</span>
				)}
			</div>
		);
	}
	return (
		<div className={clsx('grid', 'gap-1')}>
			<label
				htmlFor={name}
				className={clsx(
					'text-neutral-text-default',
					'flex',
					'gap-2',
					'justify-between',
				)}
			>
				{label}
				{!required && (
					<span className={clsx('text-neutral-text-subtle')}>
						Valgfritt
					</span>
				)}
			</label>
			<input
				id={name}
				type={type}
				placeholder={placeholder}
				{...(required && {'aria-required': true})}
				{...(error && {'aria-invalid': true})}
				{...(register && register(name, {valueAsNumber}))}
				className={clsx(
					'focus-visible:border-primary',
					'bg-white',
					'border',
					'rounded-xl',
					error
						? 'border-danger-border-strong'
						: 'border-neutral-border-default',
					'py-3',
				)}
			/>
			{error && (
				<span
					role="alert"
					className={clsx(
						'flex',
						'gap-1',
						'mt-1',
						'text-danger-text-subtle',
					)}
				>
					<FiAlertCircle
						aria-hidden
						className="text-danger-text-subtle relative top-1 shrink-0"
					/>
					{error.message}
				</span>
			)}
		</div>
	);
};
